import { DestroyRef, Directive, ElementRef, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmService } from 'excellence-ui';
import { FeatureFlagsResponse } from '../../core/clients/portal/models';
import { ConfigService } from '../../core/services/config.service';
import { OrganizationsStore } from '../../features/organizations/store/organizations.store';
import { environment } from '../../../environments/environment';

@Directive({
  selector: '[notImplemented]',
})
export class NotImplementedDirective {
  private elementRef = inject(ElementRef<HTMLElement>);
  private configService = inject(ConfigService);
  private confirmService = inject(ConfirmService);
  private translocoService = inject(TranslocoService);
  private organizationsStore = inject(OrganizationsStore);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  notImplemented = input<keyof FeatureFlagsResponse | ''>('');

  notImplementedTitle = input<string>();
  notImplementedMessage = input<string>();

  private isDialogOpen = false;

  constructor() {
    const element = this.elementRef.nativeElement;
    const handler = (event: MouseEvent) => this.handleClick(event);

    element.addEventListener('click', handler, true);
    this.destroyRef.onDestroy(() => {
      element.removeEventListener('click', handler, true);
    });
  }

  private handleClick(event: MouseEvent): void {
    if (!this.shouldIntercept()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    void this.showDialog();
  }

  private shouldIntercept(): boolean {
    const flag = this.notImplemented();

    if (flag) {
      const isEnabled = this.configService.isEnabled(flag);
      return !isEnabled;
    }

    return true;
  }

  private async showDialog(): Promise<void> {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;
    try {
      const confirm = await this.confirmService.confirm({
        title:
          this.notImplementedTitle() ||
          this.translocoService.translate('shared.notImplementedDialog.title'),
        message:
          this.notImplementedMessage() ||
          this.translocoService.translate('shared.notImplementedDialog.message'),
        mode: 'default',
        allowCancel: true,
        cancelButtonText: this.translocoService.translate(
          'shared.notImplementedDialog.cancelButton',
        ),
        confirmButtonText: this.translocoService.translate(
          'shared.notImplementedDialog.confirmButton',
        ),
        icon: 'heroInformationCircle',
      });

      if (!confirm) {
        return; // close was pressed
      }

      this.navigateToContactForm();
    } finally {
      this.isDialogOpen = false;
    }
  }

  private navigateToContactForm(): void {
    const organizationId = this.organizationsStore.selectedId();
    if (organizationId) {
      void this.router.navigate(['/organizations', organizationId, 'dashboard'], {
        queryParams: {
          showContactDialog: 'true',
        },
      });
    } else {
      window.open(environment.links.contact, '_blank');
    }
  }
}
